import type { PlayerAttributes } from '../game/types';

/**
 * Skill hit chance — base from skill, modified by luck (gameplay_ideas).
 * Result clamped to [0.05, 0.99].
 */
export function computeSkillHitChance(skillAccuracy: number, luck: number): number {
  const chance = skillAccuracy + luck * 0.015;
  return Math.min(0.99, Math.max(0.05, chance));
}

export function rollSkillHit(
  skillAccuracy: number,
  luck: number,
  rng: () => number = Math.random,
): boolean {
  return rng() < computeSkillHitChance(skillAccuracy, luck);
}

/**
 * Enemy skill accuracy vs player dodge (already computed elsewhere).
 * Hit if rng < enemyAccuracy * (1 - dodgeChance) roughly:
 * we resolve as: first roll hit by enemy accuracy, then player may dodge.
 */
export function rollEnemySkillHit(
  enemyAccuracy: number,
  rng: () => number = Math.random,
): boolean {
  const chance = Math.min(0.99, Math.max(0.05, enemyAccuracy));
  return rng() < chance;
}

/** Optional helper for UI labels. */
export function skillHitPercent(skillAccuracy: number, stats: PlayerAttributes): number {
  return Math.round(computeSkillHitChance(skillAccuracy, stats.luck) * 100);
}

/** STR 30%, DEX 30%, Luck 40%; normalized and capped at 45%. */
export function computeDodgeChance(stats: PlayerAttributes): number {
  const weighted = stats.strength * 0.3 + stats.dexterity * 0.3 + stats.luck * 0.4;
  return Math.min(0.45, Math.max(0, weighted / 50));
}

export function rollDodge(
  stats: PlayerAttributes,
  rng: () => number = Math.random,
): boolean {
  return rng() < computeDodgeChance(stats);
}
